// frontend/src/pages/LandingPage.tsx
// UPDATED VERSION - People Metrics + Screenshot Sections

import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import {
  Shield,
  TrendingUp,
  Map as MapIcon,
  Assessment,
  Email,
  CheckCircle,
  People,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';

export default function LandingPage() {
  const navigate = useNavigate();
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/contact/submit`, formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit contact form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToContact = () => {
    setShowContactForm(true);
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const features = [
    {
      icon: <Assessment sx={{ fontSize: 48, color: '#1976d2' }} />,
      title: 'Portfolio Risk Analysis',
      description: 'Comprehensive physical risk assessment across 33,000+ US postal codes with detailed hazard breakdowns.',
    },
    {
      icon: <MapIcon sx={{ fontSize: 48, color: '#1976d2' }} />,
      title: 'Interactive Risk Mapping',
      description: 'Visualize physical risks on interactive maps with location-level detail and real-time weather alerts.',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 48, color: '#1976d2' }} />,
      title: 'Time-Based Projections',
      description: '10-year climate risk projections using IPCC AR6 scenarios to understand future exposure.',
    },
    {
      icon: <People sx={{ fontSize: 48, color: '#1976d2' }} />,
      title: 'People Metrics',
      description: 'Analyze community resilience and vulnerability with demographic, housing, and healthcare data.',
    },
  ];

  const stats = [
    { value: '33,000+', label: 'US Postal Codes Covered' },
    { value: '18', label: 'Physical Hazards Analyzed' },
    { value: '200+', label: 'Environmental Metrics' },
    { value: '50+', label: 'Demographics Indicators' },
  ];

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" fontWeight="bold" gutterBottom
              sx={{ color: 'white' }} >
                Spatial Risk Q
              </Typography>
              <Typography variant="h5" sx={{ mb: 3, opacity: 0.9, color: 'white' }}>
                Enterprise Physical Risk Intelligence Platform
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', color: 'white' }}>
                Comprehensive postal code-level physical risk analysis for institutional portfolios.
                Trusted by Asset Managers, REITs, banks, insurance companies, and government entities.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={scrollToContact}
                  sx={{
                    bgcolor: 'white',
                    color: '#1976d2',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    '&:hover': { bgcolor: '#f5f5f5', transform: 'translateY(-2px)' },
                    transition: 'all 0.3s',
                  }}
                  startIcon={<Email />}
                >
                  Request Access
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    '&:hover': { 
                      borderColor: 'white', 
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s',
                  }}
                >
                  Sign In
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ bgcolor: 'white', py: 4, borderBottom: '1px solid #e5e7eb' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight="bold" color="primary">
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" fontWeight="bold" gutterBottom>
          Powerful Risk Intelligence
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Transform complex physical risk data into actionable insights for portfolio protection
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Platform Showcase Section with Screenshots */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" fontWeight="bold" gutterBottom>
            Comprehensive Risk Analysis Platform
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
            Powerful modules to understand, analyze, and mitigate physical risks
          </Typography>

          <Grid container spacing={6}>
            {/* 1. Hazard Analysis Screenshot */}
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 4, bgcolor: '#f8fafc', borderRadius: 3 }}>
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Shield sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
                      <Typography variant="h4" fontWeight="bold">
                        Detailed Hazard Analysis
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      Comprehensive breakdown of 18 climate hazards including hurricanes, earthquakes, 
                      tornadoes, floods, and wildfires. Each hazard includes:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, color: 'text.secondary' }}>
                      <li><Typography variant="body2">Risk scores from 1-10 for each location</Typography></li>
                      <li><Typography variant="body2">Asset value exposure calculations</Typography></li>
                      <li><Typography variant="body2">Portfolio percentage breakdown</Typography></li>
                      <li><Typography variant="body2">Historical event frequency analysis</Typography></li>
                      <li><Typography variant="body2">Average risk scoring across portfolio</Typography></li>
                    </Box>
                    <Button
                      variant="contained"
                      onClick={scrollToContact}
                      sx={{ mt: 3 }}
                      startIcon={<Email />}
                    >
                      Request Demo
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={4}
                      sx={{
                        bgcolor: 'white',
                        p: 2,
                        borderRadius: 2,
                        border: '2px solid #e5e7eb',
                      }}
                    >
                      {/* REPLACE THIS BOX WITH YOUR ACTUAL IMAGE */}
                      <Box
                        component="img"
                        src="/hazard-analysis-screenshot.png"
                        alt="Detailed Hazard Analysis"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          borderRadius: 1,
                          display: 'block',
                        }}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* 2. Portfolio Management Screenshot */}
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 4, bgcolor: '#f8fafc', borderRadius: 3 }}>
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                    <Paper
                      elevation={4}
                      sx={{
                        bgcolor: 'white',
                        p: 2,
                        borderRadius: 2,
                        border: '2px solid #e5e7eb',
                      }}
                    >
                      {/* REPLACE THIS BOX WITH YOUR ACTUAL IMAGE */}
                      <Box
                        component="img"
                        src="/portfolio-rankings-screenshot.png"
                        alt="Portfolio Risk Rankings"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          borderRadius: 1,
                          display: 'block',
                        }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <TrendingUp sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
                      <Typography variant="h4" fontWeight="bold">
                        Portfolio Risk Rankings
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      Advanced portfolio analytics with comparative peer benchmarking. 
                      Understand how your assets rank against national percentiles:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, color: 'text.secondary' }}>
                      <li><Typography variant="body2">Percentile rankings for all 18 hazards</Typography></li>
                      <li><Typography variant="body2">Location-by-location risk comparison</Typography></li>
                      <li><Typography variant="body2">Identify top 10% highest risk properties</Typography></li>
                      <li><Typography variant="body2">Material risk tagging system</Typography></li>
                      <li><Typography variant="body2">Export capabilities for due diligence</Typography></li>
                    </Box>
                    <Button
                      variant="contained"
                      onClick={scrollToContact}
                      sx={{ mt: 3 }}
                      startIcon={<Email />}
                    >
                      Request Demo
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* 3. People Metrics Screenshot */}
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 4, bgcolor: '#f8fafc', borderRadius: 3 }}>
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <People sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
                      <Typography variant="h4" fontWeight="bold">
                        People Metrics
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      Community resilience and vulnerability analysis to understand the human impact 
                      of climate risks on your portfolio locations:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, color: 'text.secondary' }}>
                      <li><Typography variant="body2">Resilience scores (1-10 scale)</Typography></li>
                      <li><Typography variant="body2">Vulnerability assessments</Typography></li>
                      <li><Typography variant="body2">Demographic analysis including homeownership rates</Typography></li>
                      <li><Typography variant="body2">Healthcare infrastructure metrics (hospital beds per capita)</Typography></li>
                      <li><Typography variant="body2">Special needs population data</Typography></li>
                      <li><Typography variant="body2">Civic engagement indicators</Typography></li>
                    </Box>
                    <Button
                      variant="contained"
                      onClick={scrollToContact}
                      sx={{ mt: 3 }}
                      startIcon={<Email />}
                    >
                      Request Demo
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={4}
                      sx={{
                        bgcolor: 'white',
                        p: 2,
                        borderRadius: 2,
                        border: '2px solid #e5e7eb',
                      }}
                    >
                      {/* REPLACE THIS BOX WITH YOUR ACTUAL IMAGE */}
                      <Box
                        component="img"
                        src="/people-metrics-screenshot.png"
                        alt="People Metrics Dashboard"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          borderRadius: 1,
                          display: 'block',
                        }}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Box sx={{ bgcolor: '#f8fafc', py: 8 }} id="contact">
        <Container maxWidth="md">
          <Paper elevation={6} sx={{ p: 5, borderRadius: 3 }}>
            {success ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircle sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                  Thank You!
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  We've received your request
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Our team will contact you within 1-2 business days to discuss your needs 
                  and set up your account.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    setSuccess(false);
                    setShowContactForm(false);
                  }}
                >
                  Close
                </Button>
              </Box>
            ) : (
              <>
                <Typography variant="h3" fontWeight="bold" gutterBottom align="center">
                  Request Access
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                  Fill out the form below and our team will contact you to set up your account
                </Typography>

                <Divider sx={{ mb: 4 }} />

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Message (Optional)"
                        name="message"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your use case, portfolio size, and specific needs..."
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <Email />}
                        sx={{
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {loading ? 'Submitting...' : 'Request Access'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>

                <Divider sx={{ my: 4 }} />

                <Typography variant="body2" color="text.secondary" align="center">
                  Already have an account?{' '}
                  <Button
                    variant="text"
                    onClick={() => navigate('/login')}
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 'bold',
                    }}
                  >
                    Sign In
                  </Button>
                </Typography>
              </>
            )}
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#1e3a8a', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" fontWeight="bold" gutterBottom
              color="inherit"
              >
                Spatial Risk Q
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}
              color="inherit"
              >
                Enterprise climate risk intelligence for institutional portfolios
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}
              color="inherit"
              >
                © {new Date().getFullYear()} Spatial Risk Q. All rights reserved.
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}
              color="inherit"
              >
                Powered by spatialrisksystems.com
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}